//-- Copyright (c) 2021 TMT Insights Group, LLC
import { MouseEventHandler, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { Box, Button, Grid, Typography, lighten } from '@mui/material';

import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import type { TitleEntity } from '@/redux/slices/slice_types';
import { selectVersions } from '@/redux/selectors/orderPlacementSelectors';
import { setAllVersionSelectionsByType } from '@/redux/slices/orderPlacementSlice';
import { selectActiveVersions, selectTitleDataById } from '@/redux/selectors/titleSelectors';
import PolarisIcon from '@/components/_common/PolarisIcon';
import VersionsChip from '@/components/OrderPlacement/OPStep2FileSelection/TitleBrowser/VersionChip';

type TitleBrowserItemMenuProps = {
  parentId: number;
  items: TitleEntity[];
};

// write a function that takes the array of versions above and groups them by lineage_meta.season_name
const groupVersionsBySeason = (versions: TitleEntity[]) => {
  const groupedVersions: Record<string, TitleEntity[]> = {};
  versions.forEach((version) => {
    const parent = version.lineage_meta.season_name;
    if (!parent) return;
    if (!groupedVersions[parent]) {
      groupedVersions[parent] = [version];
    } else {
      groupedVersions[parent].push(version);
    }
  });
  return groupedVersions;
};

const TitleBrowserItemMenu = ({ parentId, items = [] }: TitleBrowserItemMenuProps) => {
  const dispatch = useAppDispatch();
  const titlesById = useAppSelector(selectTitleDataById);
  const versions = useAppSelector(selectActiveVersions);
  const selectedVersions = useAppSelector(selectVersions);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [selected, setSelected] = useState<string[]>([]);
  const menuRef = useRef<HTMLDivElement>(null);
  const groupedBySeason = groupVersionsBySeason(versions);

  const handleButtonClick: MouseEventHandler<HTMLButtonElement> = (event) => {
    event.stopPropagation();
    setIsOpen(true);
  };

  const handleVersionChipClick = (versionName: string, versions: TitleEntity[]) => {
    let newSelected = [];
    let isSelected;
    if (selected.includes(versionName)) {
      newSelected = selected.filter((s) => s !== versionName);
      isSelected = true;
    } else {
      newSelected = [...selected, versionName];
      isSelected = false;
    }
    setSelected(newSelected);
    dispatch(setAllVersionSelectionsByType({ versions, titlesById, isSelected }));
  };

  // if you're here wondering why "add all" doesn't work on CR, it's because the episode `lineage_meta.season_name` is missing
  const getUniqueVersionTypes = () => {
    const seasonName = (items[0].lineage_meta?.season_name || items[0].lineage_meta?.series_name) as keyof typeof groupedBySeason;
    if (!seasonName || !groupedBySeason[seasonName]) return;
    return groupedBySeason[seasonName].reduce(
      (grouped, version) => {
        const type = (version?.version_type || version.name) as keyof typeof grouped;
        if (!type) return grouped;
        if (!grouped[type]) {
          grouped[type] = [];
        }
        grouped[type].push(version);
        return grouped;
      },
      {} as Record<string, TitleEntity[]>
    );
  };

  const uniqueVersionTypes = getUniqueVersionTypes();

  // for getting unique version groupings
  useEffect(() => {
    if (uniqueVersionTypes && Object.keys(uniqueVersionTypes).length > 0) {
      Object.keys(uniqueVersionTypes).forEach((type) => {
        const versions = uniqueVersionTypes[type];
        const hasAllElems = versions.every((version) => selectedVersions.find((sv) => sv.id === version.id));
        if (selected.includes(type)) {
          if (!hasAllElems) {
            const newSelected = selected.filter((s) => s !== type);
            setSelected(newSelected);
          }
        } else {
          if (hasAllElems) {
            const newSelected = [...selected, type];
            setSelected(newSelected);
          }
        }
      });
    }
  }, [selectedVersions]);

  // for clicking outside the component
  useEffect(() => {
    if (isOpen) {
      const handleClickOutside = (event: any) => {
        if (!menuRef?.current?.contains(event.target)) {
          setIsOpen(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);

  if (!uniqueVersionTypes || Object.keys(uniqueVersionTypes).length === 0) {
    return null;
  }

  return (
    <Wrapper>
      <Button disableRipple onClick={handleButtonClick} sx={{ mr: -2 }}>
        Add all <PolarisIcon icon="down_filled" style={{ marginLeft: 10 }} />
      </Button>
      <Menu open={isOpen} ref={menuRef}>
        <MenuContainer container>
          <Typography fontSize={11} mb={2} sx={{ opacity: 0.6 }}>
            Select Version(s)
          </Typography>
          <Grid container>
            {Object.keys(uniqueVersionTypes).map((versionKey) => (
              <VersionsChip
                key={`${parentId}_${versionKey}`}
                versionName={versionKey}
                versions={uniqueVersionTypes[versionKey]}
                isSelected={selected.includes(versionKey)}
                onClick={handleVersionChipClick}
              />
            ))}
          </Grid>
        </MenuContainer>
      </Menu>
    </Wrapper>
  );
};

const Wrapper = styled(Box)(() => ({
  position: 'relative',
  marginLeft: 'auto'
}));

const Menu = styled(Box)<{ open: boolean }>(({ theme, open }) => ({
  position: 'absolute',
  top: 40,
  right: -15,
  width: 280,
  borderRadius: 8,
  zIndex: 1,
  backgroundColor: lighten(theme.palette.background.paper, 0.04),
  boxShadow: '0 0 10 0 rgba(0, 0, 0, 0.3)',
  cursor: 'default',
  transform: open ? 'scale(1, 1)' : 'scale(0.5, 0)',
  transformOrigin: 'right top',
  transition: 'transform 0.15s ease',

  '&::before': {
    content: '""',
    position: 'absolute',
    right: 30,
    top: -5,
    borderBottom: `5px solid ${lighten(theme.palette.background.paper, 0.04)}`,
    borderLeft: '5px solid transparent',
    borderRight: '5px solid transparent'
  }
}));

const MenuContainer = styled(Grid)(() => ({
  padding: 16,
  '& .MuiGrid-root': {
    padding: 0
  }
}));

export default TitleBrowserItemMenu;
